import { Route, Routes, BrowserRouter } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import AuthenticationProvider from "@/infra/AuthenticationProvider";
import { careEnvironment, QUERY_CLIENT } from "@akina/config";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { ErrorBoundary, LoadingOverlay, MaintenanceGuard, Toaster } from "@akina/presentation";
import { IntlProvider } from "@/components/app/IntlProvider";
import { PUBLIC_ROUTES } from "@/components/app/PublicPages";
import { GUEST_ROUTES } from "@/components/app/GuestPages";
import { PROTECTED_ROUTES } from "@/components/app/ProtectedPages";
import { ConnectionStatusPage } from "@/pages/ConnectionStatusPage";
import { useInitializeDesktopApp } from "@/hooks/app/useInitializeDesktopApp";
import { useInitializeAnalytics } from "@/hooks/app/useInitializeAnalytics";
import { useInitializeIntl } from "@/infra/locale";
import { akinaAsciiArt } from "@/components/asciiArt";

export function App() {
    useInitializeIntl();
    useInitializeAnalytics();
    useInitializeDesktopApp();
    useEffect(() => { akinaAsciiArt(); }, []);

    return (
        <Routes>
            <Route element={<ConnectionStatusPage />}>
                {PUBLIC_ROUTES}
                {GUEST_ROUTES}
                {PROTECTED_ROUTES}
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
    );
}

export function AppProviders({ children }: Readonly<{ children: React.ReactNode }>) {
    return (
        <ErrorBoundary env={careEnvironment.environment}>
            <Suspense fallback={<LoadingOverlay />}>
                <QueryClientProvider client={QUERY_CLIENT}>
                    <IntlProvider>
                        <Toaster />
                        <MaintenanceGuard>
                            <BrowserRouter>
                                <AuthenticationProvider>
                                    {children}
                                </AuthenticationProvider>
                            </BrowserRouter>
                        </MaintenanceGuard>
                    </IntlProvider>
                </QueryClientProvider>
            </Suspense>
        </ErrorBoundary>
    );
}
